import { useState } from "react";
import { motion } from "framer-motion";

const ANALYTICS_IMG = "/screenshots/analytics.png";
const DETAIL_IMG = "/screenshots/dashboard-detail.png";
const RELEASE_IMG = "/screenshots/release.png";

const Frame = ({ src, alt, label, testid, height = "md:h-[340px]" }) => (
  <div className="relative border border-border overflow-hidden">
    <img src={src} alt={alt} data-testid={testid} className={`w-full h-auto ${height} md:object-cover md:object-top`} />
    <div className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase bg-background/80 backdrop-blur px-3 py-1.5 border border-border">
      {label}
    </div>
  </div>
);

export const Showcase = () => {
  const [pos, setPos] = useState({ x: 50, y: 50 });

  return (
    <section id="showcase" className="py-24 md:py-32 border-t border-border" data-testid="showcase-section">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <h2 className="font-display font-semibold tracking-normal [word-spacing:0.12em] text-3xl md:text-5xl" data-testid="showcase-heading">
            INSIDE THE <span className="text-accent">TERMINAL</span>
          </h2>
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
            Real screens from Operation Blue v1.0
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-border overflow-hidden cursor-crosshair"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setPos({
              x: ((e.clientX - r.left) / r.width) * 100,
              y: ((e.clientY - r.top) / r.height) * 100,
            });
          }}
          data-testid="showcase-frame"
        >
          <img
            src={ANALYTICS_IMG}
            alt="YIIS-100 portfolio analytics screen"
            data-testid="showcase-image"
            className="w-full h-auto md:h-[600px] md:object-cover md:object-top"
          />
          <div
            className="absolute inset-0 pointer-events-none transition-opacity duration-300"
            style={{
              background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, hsl(var(--accent) / 0.16), transparent 42%)`,
            }}
          />
          <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-background/80 backdrop-blur px-3 py-1.5 border border-border">
            FIG. 01 — PORTFOLIO ANALYTICS
          </div>
          <div className="hidden md:block absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.25em] uppercase bg-background/80 backdrop-blur px-3 py-1.5 border border-border tabular-nums">
            X {pos.x.toFixed(0)} / Y {pos.y.toFixed(0)}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Frame src={DETAIL_IMG} alt="YIIS-100 master dashboard detail" label="FIG. 02 — MASTER DASHBOARD" testid="showcase-image-secondary" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            <Frame src={RELEASE_IMG} alt="YIIS-100 release history screen" label="FIG. 03 — RELEASE LIFECYCLE" testid="showcase-image-tertiary" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
